'use client';

import { useState } from 'react';

import { Check } from 'lucide-react';

import DemoForm from '@/components/demo/demo-form';
import MeetingScheduler, {
  type SchedulerHandoff,
} from '@/components/demo/meeting-scheduler';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';
import { getRegionFromCookie, type VisitorRegion } from '@/lib/regions';

/* Copy that only exists on this page's post-submit step, kept locale-local the
 * same way logos.tsx keeps its section copy. English is the source of truth.
 * The Arabic heading is the page's h1 in this state, so it keeps its warmth
 * — on the verb (`تمام`, `اختر`) and on the function words too (`اللي`, `بس`):
 * the house voice is Najdi on both classes. */
const HANDOFF_COPY = {
  en: {
    heading: 'Great — now pick a time that works for you',
    sub: 'Your details are already filled in — just choose a slot below and you’re all set.',
  },
  ar: {
    heading: 'تمام! اختر الوقت اللي يناسبك',
    sub: 'بياناتك مسجّلة عندنا — بس اختر موعد من المواعيد المتاحة بالأسفل.',
  },
};

/* Social proof is market-specific, like the logo wall on the home page:
 * English leads with the European names, Arabic with the regional ones. */
const LOGOS = {
  en: [
    { src: '/logos/logo-mollie.svg', alt: 'Mollie', className: 'h-5' },
    { src: '/logos/logo-ticketswap.svg', alt: 'TicketSwap', className: 'h-6' },
    { src: '/logos/logo-moneygram.svg', alt: 'MoneyGram', className: 'h-5' },
  ],
  ar: [
    { src: '/logos/logo-qoyod.png', alt: 'Qoyod', className: 'h-6' },
    { src: '/logos/logo-oto.svg', alt: 'OTO', className: 'h-6' },
    { src: '/logos/logo-rewaa.svg', alt: 'Rewaa', className: 'h-5' },
  ],
};

const DemoPage = () => {
  const { t, locale } = useTranslation();
  const copy = locale === 'ar' ? HANDOFF_COPY.ar : HANDOFF_COPY.en;
  const logos = locale === 'ar' ? LOGOS.ar : LOGOS.en;
  const [handoff, setHandoff] = useState<SchedulerHandoff | null>(null);
  // Region comes from a cookie (SSR-safe: getRegionFromCookie returns 'Others'
  // on the server). It's only used in the submit payload and the post-submit
  // scheduler — never rendered at hydration time — so a lazy initializer is
  // safe and avoids a setState-in-effect.
  const [region] = useState<VisitorRegion>(() => getRegionFromCookie());

  // Text content section
  const textSection = (
    <div className="w-full pb-10 text-start md:space-y-10 md:pb-0 lg:w-1/2">
      <div className="space-y-5 md:max-w-[40rem]">
        <h1 className="font-serif text-balance text-neutral-900 text-2xl sm:text-[1.75rem] md:text-[2rem]">
          {handoff
            ? copy.heading
            : t.demo[
                'Let us know about yourself, and we will reach out to you soon!'
              ]}
        </h1>
        <div className="text-sm leading-relaxed text-neutral-500 md:text-base">
          {handoff
            ? copy.sub
            : t.demo[
                "Share some details about your team and support needs. We'll get back to you with next steps."
              ]}
        </div>
      </div>
      <div className="hidden md:block">
        <div className="space-y-16 pb-20 lg:pb-0">
          <div className="space-y-6">
            <div className="mt-16 flex overflow-hidden"></div>
            <div className="space-y-4">
              <p className="text-sm font-semibold">
                {t.demo['What you can expect:']}
              </p>
              <div className="flex items-center gap-2.5">
                <Check className="text-muted-foreground size-5 shrink-0" />
                <p className="text-sm">
                  {t.demo['Live demo of AI automating actual support tickets']}
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <Check className="text-muted-foreground size-5 shrink-0" />
                <p className="text-sm">
                  {t.demo['See how it works with your existing helpdesk']}
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <Check className="text-muted-foreground size-5 shrink-0" />
                <p className="text-sm">
                  {t.demo['Get answers to your specific use case']}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-8">
            {logos.map((logo) => (
              <img
                key={logo.src}
                src={logo.src}
                alt={logo.alt}
                className={`${logo.className} opacity-60`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const formSection = (
    <div className="flex w-full justify-center lg:mt-2.5 lg:w-1/2">
      <div className="relative flex w-full max-w-[30rem] min-w-[20rem] flex-col items-center overflow-visible md:min-w-[24rem]">
        <div className="relative z-10 w-full overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)]">
          {handoff ? (
            <div className="p-4 sm:p-5">
              <MeetingScheduler handoff={handoff} />
            </div>
          ) : (
            <DemoForm
              region={region}
              locale={locale ?? 'en'}
              onSuccess={setHandoff}
            />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section className="relative py-42">
        {/* Background gradients */}
        <div className="pointer-events-none absolute inset-x-0 -top-20 -bottom-20 bg-[radial-gradient(ellipse_35%_15%_at_40%_55%,hsl(var(--accent))_0%,transparent_100%)] lg:bg-[radial-gradient(ellipse_12%_20%_at_60%_45%,hsl(var(--accent))_0%,transparent_100%)]"></div>
        <div className="pointer-events-none absolute inset-x-0 -top-20 -bottom-20 bg-[radial-gradient(ellipse_35%_20%_at_70%_75%,hsl(var(--accent))_0%,transparent_80%)] lg:bg-[radial-gradient(ellipse_15%_30%_at_70%_65%,hsl(var(--accent))_0%,transparent_80%)]"></div>
        {/* Background pattern */}
        <div className="pointer-events-none absolute inset-x-0 -top-20 -bottom-20 bg-[radial-gradient(hsl(var(--accent-foreground)/0.1)_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_60%_60%_at_65%_50%,#000_0%,transparent_80%)] [background-size:8px_8px]"></div>
        <div className="container flex w-full flex-col gap-x-32 overflow-hidden lg:flex-row">
          {textSection}
          {formSection}
        </div>
      </section>

      <Testimonials />
    </>
  );
};

export default DemoPage;
